import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { getReviewDateStatus } from "./qmsReviewDateUtils";

export function getMyLearningDocumentCode(row) {
  const typeAbbr = row?.DocumentTypeAbberiviation || "";
  const catAbbr = row?.DocumentCategoryAbberiviation || "";
  const uniqueNum = row?.DocumentCategoryUniqueNumber || "";
  const version = row?.VersionNumber ?? "";
  if (!typeAbbr && !catAbbr && !uniqueNum && version === "") return "—";
  return `${typeAbbr}_${catAbbr}_${uniqueNum}_V${version}`;
}

/**
 * Legacy DMS certificate document label:
 * TYPE_CAT_NUM_V_VERSION_TitleWithoutExtension
 */
export function getMyLearningCertificateDocumentName(row) {
  const typeAbbr = String(row?.DocumentTypeAbberiviation || "").trim();
  const catAbbr = String(row?.DocumentCategoryAbberiviation || "").trim();
  const uniqueNum = String(row?.DocumentCategoryUniqueNumber || "").trim();
  const version = String(row?.VersionNumber ?? "").trim();
  const titleRaw = String(row?.DocumentTitle || row?.DocumentTitleWithExt || "").trim();
  const titleBase = titleRaw.includes(".")
    ? titleRaw.split(".").slice(0, -1).join(".")
    : titleRaw;

  if (typeAbbr || catAbbr || uniqueNum || version) {
    return `${typeAbbr}_${catAbbr}_${uniqueNum}_V_${version}_${titleBase}`;
  }
  return titleBase || "Document";
}

export function isMyLearningCompleted(row) {
  if (String(row?.LearningStatus || "").trim() === "Completed") return true;
  const acknowledged = Number(row?.IsAcknowledged) === 1 || row?.IsAcknowledged === true;
  const quizRequired = Number(row?.IsQuizApplicable) === 1 || row?.IsQuizApplicable === true;
  const passed = Number(row?.IsPassed) === 1 || row?.IsPassed === true;
  return acknowledged && (!quizRequired || passed);
}

/** AttemptLimit: 0 / missing = unlimited. AttemptsUsed comes from hub MyLearningDocument. */
export function getMyLearningQuizAttemptState(detail, quizMeta = {}) {
  const attemptLimit = Number(quizMeta?.AttemptLimit ?? detail?.AttemptLimit ?? 0) || 0;
  const attemptsUsed = Number(detail?.AttemptsUsed ?? 0) || 0;
  const unlimited = attemptLimit <= 0;
  const remaining = unlimited ? null : Math.max(0, attemptLimit - attemptsUsed);
  const limitReached = !unlimited && attemptsUsed >= attemptLimit;

  return {
    attemptLimit,
    attemptsUsed,
    remaining,
    unlimited,
    limitReached,
  };
}

export function getMyLearningQuizAttemptLimitMessage(attemptState) {
  if (!attemptState || attemptState.unlimited) return "";
  if (attemptState.limitReached) {
    return `You have used all ${attemptState.attemptLimit} quiz attempt(s) for this document. Further retakes are not allowed.`;
  }
  return `Attempts used: ${attemptState.attemptsUsed} of ${attemptState.attemptLimit}`;
}

export function getMyLearningUnifiedStatusMeta(row) {
  if (isMyLearningCompleted(row)) {
    return { label: "Completed", tone: "success" };
  }
  const review = getReviewDateStatus(row);
  if (review.status === "overdue") {
    return { label: "Overdue", tone: "danger" };
  }
  return { label: "To do", tone: "warning" };
}

export function getMyLearningStatusMeta(row) {
  if (isMyLearningCompleted(row)) {
    return { label: "Completed", tone: "success" };
  }
  const quizRequired = Number(row?.IsQuizApplicable) === 1 || row?.IsQuizApplicable === true;
  if (quizRequired) {
    return { label: "Quiz required", tone: "warning" };
  }
  const mandatory = Number(row?.IsReadingMandatory) === 1 || row?.IsReadingMandatory === true;
  if (mandatory) {
    return { label: "Acknowledgement required", tone: "info" };
  }
  return { label: "To do", tone: "neutral" };
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function formatMyLearningCertificateDate(value) {
  const raw = String(value || "").trim();
  if (!raw) return "—";
  if (/^\d{2}\/\d{2}\/\d{4}/.test(raw)) return raw.slice(0, 10);
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  const dd = String(parsed.getDate()).padStart(2, "0");
  const mm = String(parsed.getMonth() + 1).padStart(2, "0");
  const yyyy = parsed.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

const NHS_LOGO_ASPECT = 150.79 / 384.93;
const NHS_LOGO_DISPLAY_WIDTH = 190;
const NHS_LOGO_DISPLAY_HEIGHT = Math.round(NHS_LOGO_DISPLAY_WIDTH * NHS_LOGO_ASPECT);
const AQ_LOGO_DISPLAY_WIDTH = 150;

function getCertificateLogoUrls() {
  const publicBase = String(process.env.PUBLIC_URL || "").replace(/\/$/, "");
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";
  // Absolute URLs so logos work inside iframe srcDoc (relative paths break there).
  const base = `${origin}${publicBase}/images/CertificateLogo`;
  return {
    nhs: `${base}/rfl-nhs-logo.svg`,
    aq: `${base}/AscensionQLogo.png`,
  };
}

function loadImageElement(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = "async";
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`));
    img.src = src;
  });
}

/** Rasterize logo to PNG data-URL so html2canvas captures the full org logo. */
async function rasterizeLogoToPngDataUrl(src, displayWidth, displayHeight) {
  const img = await loadImageElement(src);
  const naturalW = img.naturalWidth || displayWidth;
  const naturalH = img.naturalHeight || displayHeight;
  const ratio = naturalH / naturalW || displayHeight / displayWidth;
  const width = displayWidth;
  const height = Math.max(1, Math.round(width * ratio));
  const scale = 3;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return {
    dataUrl: canvas.toDataURL("image/png"),
    width,
    height,
  };
}

async function prepareCertificateHtmlForPdf(certificateHtml) {
  const logos = getCertificateLogoUrls();
  const host = document.createElement("div");
  host.innerHTML = String(certificateHtml || "").trim();

  const images = [...host.querySelectorAll("img")];
  await Promise.all(
    images.map(async (img) => {
      const src = String(img.getAttribute("src") || "").trim();
      if (!src) return;
      const isNhs = /rfl-nhs-logo/i.test(src) || src === logos.nhs;
      const targetW = isNhs ? NHS_LOGO_DISPLAY_WIDTH : AQ_LOGO_DISPLAY_WIDTH;
      const targetH = isNhs ? NHS_LOGO_DISPLAY_HEIGHT : Math.round(targetW * 0.45);
      try {
        const raster = await rasterizeLogoToPngDataUrl(src, targetW, targetH);
        img.setAttribute("src", raster.dataUrl);
        img.setAttribute("width", String(raster.width));
        img.setAttribute("height", String(raster.height));
        img.style.width = `${raster.width}px`;
        img.style.height = `${raster.height}px`;
        img.style.maxWidth = "none";
        img.style.display = "inline-block";
      } catch (error) {
        console.warn("Certificate logo rasterize failed", error?.message || error);
      }
    }),
  );

  return host.innerHTML;
}

/**
 * Classic DMS layout (reference image 2):
 * NHS top-right, centred title/body, Powered By AQ + logo at bottom.
 */
export function buildClassicMyLearningCertificateHtml(context) {
  const logos = getCertificateLogoUrls();
  const staffName = escapeHtml(String(context.staffName || "").trim() || "Staff");
  const documentTitle = escapeHtml(
    String(context.documentTitle || "").trim() || "Document",
  );
  const completedOn = escapeHtml(String(context.completedOn || "").trim() || "—");

  return `
    <div id="DMS_Certificateprint" class="qms-cert-sheet" style="box-sizing:border-box;width:100%;max-width:720px;margin:0 auto;padding:12px;background:#fff;">
      <div class="redborderdiv" style="box-sizing:border-box;width:100%;border:2px solid #e11d2e;border-radius:14px;box-shadow:0 0 0 4px #fff, 0 0 0 6px #e11d2e;padding:20px 24px 28px;background:#fff;font-family:Arial,Helvetica,sans-serif;color:#111;overflow:visible;">
        <div style="width:100%;text-align:right;margin:4px 0 12px;min-height:${NHS_LOGO_DISPLAY_HEIGHT}px;overflow:visible;">
          <img
            src="${logos.nhs}"
            alt="Royal Free London NHS Foundation Trust"
            width="${NHS_LOGO_DISPLAY_WIDTH}"
            height="${NHS_LOGO_DISPLAY_HEIGHT}"
            style="width:${NHS_LOGO_DISPLAY_WIDTH}px;height:${NHS_LOGO_DISPLAY_HEIGHT}px;max-width:none;display:inline-block;object-fit:contain;object-position:right center;background:#fff;"
          />
        </div>
        <div style="width:100%;text-align:center;padding:8px 12px 0;">
          <div style="font-size:28px;font-weight:700;color:#c41e3a;margin:10px 0 22px;line-height:1.25;">Certificate of Completion</div>
          <div style="font-size:16px;margin:14px 0;">This is certify that</div>
          <div style="font-size:28px;font-weight:700;margin:18px 0;line-height:1.3;">${staffName}</div>
          <div style="font-size:16px;margin:14px 0;">Has Successfully completed</div>
          <div style="font-size:18px;font-weight:700;margin:18px 0;line-height:1.35;word-break:break-word;">${documentTitle}</div>
          <div style="font-size:16px;margin:14px 0;">on</div>
          <div style="font-size:18px;font-weight:700;margin:12px 0 0;">${completedOn}</div>
        </div>
        <div style="width:100%;text-align:center;margin-top:64px;padding-top:8px;">
          <div style="font-size:14px;font-weight:700;margin-bottom:10px;">Powered By AQ</div>
          <img
            src="${logos.aq}"
            alt="ascensionQ"
            width="${AQ_LOGO_DISPLAY_WIDTH}"
            style="width:${AQ_LOGO_DISPLAY_WIDTH}px;height:auto;max-width:none;display:inline-block;"
          />
        </div>
      </div>
    </div>
  `.trim();
}

/**
 * My Learning certificates always use the classic layout (image 2).
 * Modern QMS config templates (CERTIFICATE / OF COMPLETION) are not used here.
 */
export function buildMyLearningCertificateHtml(_template, context) {
  return buildClassicMyLearningCertificateHtml(context || {});
}

/** Full HTML document for iframe preview (correct visual layout). */
export function buildMyLearningCertificatePrintDocument(certificateHtml) {
  const body = String(certificateHtml || "").trim();
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>Certificate of Completion</title>
  <style>
    html, body {
      margin: 0;
      padding: 0;
      background: #fff;
      color: #111;
      font-family: Arial, Helvetica, sans-serif;
    }
    body {
      padding: 18px 16px 28px;
    }
    img {
      max-width: none;
      height: auto;
      display: inline-block;
    }
    .redborderdiv {
      width: 98% !important;
      margin: 0 auto;
      overflow: visible !important;
    }
  </style>
</head>
<body>${body}</body>
</html>`;
}

/** Build a downloadable PDF from the certificate HTML (on demand). */
export async function renderMyLearningCertificatePdfBlob(certificateHtml) {
  const preparedHtml = await prepareCertificateHtmlForPdf(certificateHtml);
  if (!preparedHtml) {
    throw new Error("Certificate content is empty.");
  }

  const host = document.createElement("div");
  host.setAttribute("data-qms-cert-pdf-host", "1");
  host.style.cssText =
    "position:fixed;left:-12000px;top:0;width:794px;padding:24px;background:#fff;z-index:-1;pointer-events:none;overflow:visible;";
  host.innerHTML = preparedHtml;
  document.body.appendChild(host);

  try {
    const images = [...host.querySelectorAll("img")];
    await Promise.all(
      images.map(
        (img) =>
          new Promise((resolve) => {
            if (img.complete && img.naturalWidth > 0) {
              resolve();
              return;
            }
            img.onload = () => resolve();
            img.onerror = () => resolve();
          }),
      ),
    );

    // Let layout settle after PNG swap.
    await new Promise((resolve) => window.setTimeout(resolve, 50));

    const canvas = await html2canvas(host, {
      scale: 2,
      useCORS: true,
      allowTaint: true,
      backgroundColor: "#ffffff",
      logging: false,
      imageTimeout: 15000,
      width: host.scrollWidth,
      height: Math.max(host.scrollHeight, host.offsetHeight),
      windowWidth: host.scrollWidth,
      windowHeight: Math.max(host.scrollHeight, host.offsetHeight),
      onclone: (clonedDoc) => {
        const frame = clonedDoc.querySelector(".redborderdiv");
        if (frame) {
          // border-radius can clip logos in html2canvas — keep double-border look without clip.
          frame.style.borderRadius = "0";
          frame.style.overflow = "visible";
        }
        clonedDoc.querySelectorAll("img").forEach((img) => {
          img.style.maxWidth = "none";
          img.style.objectFit = "contain";
        });
      },
    });

    const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 10;
    const usableWidth = pageWidth - margin * 2;
    const usableHeight = pageHeight - margin * 2;
    const imgWidth = usableWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    const imgData = canvas.toDataURL("image/png");

    let heightLeft = imgHeight;
    let position = margin;

    pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
    heightLeft -= usableHeight;

    while (heightLeft > 2) {
      position = margin - (imgHeight - heightLeft);
      pdf.addPage();
      pdf.addImage(imgData, "PNG", margin, position, imgWidth, imgHeight);
      heightLeft -= usableHeight;
    }

    return pdf.output("blob");
  } finally {
    if (host.parentNode) {
      host.parentNode.removeChild(host);
    }
  }
}

export function downloadBlobAsFile(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export const MY_LEARNINGS_STATUS_FILTER_OPTIONS = [
  { value: "", label: "All" },
  { value: "ToDo", label: "To do" },
  { value: "Completed", label: "Completed" },
  { value: "Overdue", label: "Overdue" },
];

export function parseDocumentIdNumberSearch(value) {
  return String(value || "").replace(/\D/g, "");
}

export const MY_LEARNINGS_TABS = [
  { id: "All", label: "All" },
  { id: "Pending", label: "To do" },
  { id: "Completed", label: "Completed" },
];
